import { Injectable, inject } from "@angular/core";
import Graphic from "@arcgis/core/Graphic";
import type { AreaUnit } from "@arcgis/core/core/units";
import Extent from "@arcgis/core/geometry/Extent";
import type Geometry from "@arcgis/core/geometry/Geometry";
import Multipoint from "@arcgis/core/geometry/Multipoint";
import Point from "@arcgis/core/geometry/Point";
import Polygon from "@arcgis/core/geometry/Polygon";
import Polyline from "@arcgis/core/geometry/Polyline";
import SpatialReference from "@arcgis/core/geometry/SpatialReference";
import { GeometryUnion } from "@arcgis/core/geometry/types";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import { ObjectUtils } from "~app/common/utils/object-utils";
import {
    areaOperator,
    centroidOperator,
    containsOperator,
    intersectionOperator,
    lengthOperator,
    simplifyOGCOperator,
    simplifyOperator,
    unionOperator
} from "../geometry-operators";
import { MapExtent } from "../map.models";
import {
    IMapGeometry,
    IMapGeometryLine,
    IMapGeometryPoint,
    IMapGeometryWicket,
    IMapIcon
} from "../map.settings";
import { SymbolService } from "./symbol.service";
import { TerraformerService } from "./terraformer.service";

@Injectable({
    providedIn: "root"
})
export class SpatialService {
    private readonly symbolService = inject(SymbolService);
    private readonly terraformerService = inject(TerraformerService);

    private readonly srid = 3006;
    private readonly spatialReference: SpatialReference;

    constructor() {
        this.spatialReference = this.getSpatialReference();
    }

    getSpatialReference(): SpatialReference {
        return new SpatialReference({ wkid: this.srid });
    }

    toArcGis(geometry: IMapGeometry, layerColor?: any, symbols?: any[], icons?: IMapIcon[]) {
        let arcGeometry: Graphic;
        if ((geometry as IMapGeometryPoint).northing !== undefined) {
            arcGeometry = this.fromXY(geometry as IMapGeometryPoint, layerColor, symbols, icons);
        } else if ((geometry as IMapGeometryLine).startNorthing !== undefined) {
            arcGeometry = this.fromLine(geometry as IMapGeometryLine, layerColor);
        } else {
            arcGeometry = this.fromWicket(geometry as IMapGeometryWicket, layerColor, symbols, icons);
        }

        // Text borde tas bort från symbol då den inte finns i arcgis typen
        // Any typade object bör göras om till korrekt typ utan extra fält
        const symbol = arcGeometry?.symbol as any;
        if (symbol.text === "[insert text]") {
            symbol.text = geometry.symbolText;
        }

        return arcGeometry;
    }

    convertToGraphic(geometry: IMapGeometry) {
        const ag = this.toArcGis(geometry);

        switch (ag?.geometry?.type) {
            case "polygon":
                return new Polygon(ag.geometry);
            case "extent":
                return new Extent(ag.geometry);
            case "polyline":
                return new Polyline(ag.geometry);
            case "point":
                return new Point(ag.geometry);
            case "multipoint":
                return new Multipoint(ag.geometry);
            default:
                throw new Error(`Unknown type`);
        }
    }

    toRectangle(arrayOfPoints: any[], symbol: any) {
        if (arrayOfPoints?.length !== 2) {
            return undefined;
        }
        const a1 = arrayOfPoints[0]; // övre vänstra
        const a2 = arrayOfPoints[1]; // nedre högra

        // Rektangelns hörn
        const p1 = a1;
        const p2 = [
            a2[0],
            a1[1]
        ];
        const p3 = a2;
        const p4 = [
            a1[0],
            a2[1]
        ];

        const ring = [];
        ring.push(p1);
        ring.push(p2);
        ring.push(p3);
        ring.push(p4);

        return this.toPolygon(ring, symbol);
    }

    simplifyOgc(geometry: GeometryUnion) {
        return simplifyOGCOperator.execute(geometry);
    }

    toPolyline(arrayOfPoints: any[], symbol: any) {
        return ({
            geometry: new Polyline({
                paths: arrayOfPoints,
                spatialReference: this.spatialReference
            }),
            symbol: symbol
        });
    }

    toPolygon(arrayOfPoints: any[], symbol: any, close = true) {
        const ring = arrayOfPoints;
        if (close) {
            ring.push(arrayOfPoints[0]);
        }
        const rings = [ring];
        return new Graphic(
            {
                geometry: new Polygon({
                    hasZ: false,
                    hasM: false,
                    rings: rings,
                    spatialReference: this.spatialReference
                }),
                symbol: symbol,
                attributes: {}
            });
    }

    verifyGeoId(graphic: Graphic) {
        if (!graphic.attributes.geoid) {
            graphic.setAttribute("geoid", this.generateGeoId());
        }
    }

    toRask(graphic: Graphic, returnAttributes = false): IMapGeometryWicket {
        const wkt = this.terraformerService.arcGisToWkt(graphic.geometry);
        return {
            wkt: wkt,
            id: graphic.attributes ? graphic.attributes.id : null,
            geoid: graphic.attributes && graphic.attributes.geoid ? graphic.attributes.geoid : this.generateGeoId(),
            label: graphic.attributes ? graphic.attributes.label : null,
            attributes: returnAttributes ? graphic.attributes : null,
            isDrawing: graphic.attributes ? graphic.attributes.isDrawing : false,
            tag: graphic.attributes ? graphic.attributes.tag : undefined
        };
    }

    toRaskPoint(graphic: Graphic, returnAttributes = false): IMapGeometryPoint {
        if (graphic?.geometry.type !== "point") {
            console.error("graphic is not a point");
        }
        const point = graphic.geometry as Point;
        return {
            id: graphic.attributes ? graphic.attributes.id : null,
            geoid: graphic.attributes && graphic.attributes.geoid ? graphic.attributes.geoid : this.generateGeoId(),
            label: graphic.attributes ? graphic.attributes.label : null,
            attributes: returnAttributes ? graphic.attributes : null,
            northing: point.y,
            easting: point.x
        };
    }

    toRaskGeometry(graphic: Graphic, returnAttributes = false): IMapGeometry {
        switch (graphic.geometry.type) {
            case "polygon":
            case "polyline":
            case "extent":
            case "mesh":
            case "multipoint":
                return this.toRask(graphic, returnAttributes);
            case "point": {
                return this.toRaskPoint(graphic, returnAttributes);
            }
            default:
                console.error(`Unknown graphic geometry.`, graphic);
                return null;
        }
    }

    toRaskGeometries(graphics: any[], returnAttributes = false): IMapGeometry[] {
        return graphics.map((graphic) => this.toRaskGeometry(graphic, returnAttributes));
    }

    getExtentDeprecated(graphics: any[]) {
        return this.getArcGisExtent(graphics);
    }

    getExtent(graphics: any): MapExtent {
        const x = this.getArcGisExtent(graphics);
        return new MapExtent(x.xmin, x.ymin, x.xmax, x.ymax, x.spatialReference);
    }

    getArcGisExtent(graphics: any[]) {
        if (graphics.length === 1 && graphics[0].geometry.type === "point") {
            graphics = this.boxifyPoint(graphics[0]);
        }
        const arcGraphic = this.joinGraphics(graphics);
        return arcGraphic.extent;
    }

    boxifyPoint(arcgis: any): any[] {
        if (arcgis?.geometry?.type === "point") {
            const factor = 400;
            const point2 = new Point({
                x: arcgis.geometry.x + factor,
                y: arcgis.geometry.y + factor
            });
            arcgis.geometry.x -= factor;
            arcgis.geometry.y -= factor;
            return [
                arcgis,
                { geometry: point2 }
            ];
        }
        return [arcgis];
    }

    findGeometriesWithin(arcGraphicLayer: any, selectionPolygon: any): any[] {
        const intersectingGraphics = [];
        if (arcGraphicLayer.visible) {
            const graphics = arcGraphicLayer.graphics.items;
            graphics.forEach(graphic => {
                if (containsOperator.execute(selectionPolygon.geometry, graphic.geometry)) {
                    intersectingGraphics.push(graphic);
                }
            });
        }
        return intersectingGraphics;
    }

    generateGeoId(): number {

        // TODO: internal geo id can be replaced with arcgis own internal id
        // eslint-disable-next-line sonarjs/pseudo-random
        return Math.trunc(Math.random() * 1000000 + 1000);
    }

    extentToWktPolygon(extent: any): string {
        const bottomLeft = `${extent.minX} ${extent.minY}`;
        const bottomRight = `${extent.maxX} ${extent.minY}`;
        const topRight = `${extent.maxX} ${extent.maxY}`;
        const topLeft = `${extent.minX} ${extent.maxY}`;

        return `POLYGON ((${bottomLeft},${bottomRight},${topRight},${topLeft},${bottomLeft}))`;
    }

    wktPolygonToExtent(wkt: string) {
        const parts = wkt.replace("POLYGON", "").replace("((", "").split(",").slice(0, 4);

        const bottomLeft = parts[0].trim().split(" ");
        const topRight = parts[2].trim().split(" ");

        return {
            maxX: +topRight[0],
            maxY: +topRight[1],
            minX: +bottomLeft[0],
            minY: +bottomLeft[1]
        };
    }

    join(geometries: IMapGeometry[], simplify = false): IMapGeometryWicket {
        const arcGraphics = geometries.map(g => {
            const arcGraphic = this.toArcGis(g);
            if (simplify) {
                arcGraphic.geometry = simplifyOperator.execute(arcGraphic.geometry);
            }
            return arcGraphic;
        });
        const arcGeometry = this.joinGraphics(arcGraphics);
        return this.toRask({ geometry: arcGeometry } as unknown as Graphic);
    }

    joinGraphics(graphics: Graphic[]) {
        return unionOperator.executeMany(graphics.map(g => g.geometry));
    }

    getType(geometry: IMapGeometry) {
        const arcGrapics = this.toArcGis(geometry);
        return arcGrapics?.geometry?.type;
    }

    getArea(geometry: IMapGeometry, unit: AreaUnit = "square-kilometers"): number {
        return this.getAreaFromGraphic(this.toArcGis(geometry), unit);
    }

    getAreaFromGraphic(arcGraphic: Graphic, unit: AreaUnit = "square-kilometers"): number {
        if (arcGraphic?.geometry?.type !== "polygon") {
            return 0;
        }
        return areaOperator.execute(simplifyOperator.execute(arcGraphic.geometry), { unit: unit });
    }

    getLength(geometry: IMapGeometry): number {
        const arcGeometry = this.toArcGis(geometry);
        return this.getLengthFromGraphic(arcGeometry);
    }

    getLengthFromGraphic(arcGraphic: any): number {
        return arcGraphic?.geometry?.type === "polyline"
            ? lengthOperator.execute(simplifyOperator.execute(arcGraphic.geometry), { unit: "kilometers" })
            : 0;
    }

    getCenterPointAsWkt(geometry: IMapGeometry): string {
        if (ObjectUtils.isNullOrUndefined(geometry)) {
            return null;
        }
        const arcPoint = this.getCenterPointFromGraphic(this.toArcGis(geometry));
        if (ObjectUtils.isNullOrUndefined(arcPoint)) {
            return null;
        }
        return this.terraformerService.arcGisToWkt(arcPoint);
    }

    getCenterPointFromGraphic(arcGraphic: Graphic): Point {
        if (ObjectUtils.isNullOrUndefined(arcGraphic)) {
            return null;
        }
        const extent = this.getArcGisExtent([arcGraphic]);
        return extent?.center ?? null;
    }

    getCenterPointFromMapExtent(mapExtent: MapExtent): IMapGeometryWicket {
        if (ObjectUtils.isNullOrUndefined(mapExtent)) {
            return null;
        }
        const x = mapExtent.minX + (mapExtent.maxX - mapExtent.minX) / 2;
        const y = mapExtent.minY + (mapExtent.maxY - mapExtent.minY) / 2;
        return this.toRask({
            geometry: new Point({
                x: x,
                y: y,
                spatialReference: this.getSpatialReference()
            })
        } as unknown as Graphic);
    }

    getArcGraphic(raskGeometry: IMapGeometry, graphicLayers: GraphicsLayer[]): Graphic {
        const graphic = this.getArcLayerContaining(raskGeometry, graphicLayers)?.graphics.find(i => i.attributes.id === raskGeometry.id);

        if (!graphic) {
            console.error("Graphic not found");
        }
        return graphic;
    }

    getArcLayerContaining(raskGeometry: IMapGeometry, graphicLayers: GraphicsLayer[]) {
        return graphicLayers.find(l => l.graphics?.find(i => i.attributes.id === raskGeometry.id));
    }

    updateArcGraphic(arcGraphic: Graphic, geowicket: IMapGeometryWicket) {
        arcGraphic.attributes = this.getMetadata(geowicket);
    }

    getAreaDifference(wkt1: string, wkt2: string): number {
        const geom1 = this.terraformerService.wktToArcgis(wkt1, this.spatialReference);
        const geom2 = this.terraformerService.wktToArcgis(wkt2, this.spatialReference);

        const area51 = areaOperator.execute(geom1, { unit: "hectares" });
        const area52 = areaOperator.execute(geom2, { unit: "hectares" });

        return area51 - area52;
    }

    getAreaWkt(wkt: string): number {
        const geom = this.terraformerService.wktToArcgis(wkt, this.spatialReference);
        return areaOperator.execute(geom, { unit: "hectares" });
    }

    centroid(geom: GeometryUnion): Point {
        return centroidOperator.execute(geom);
    }

    getNetAreaHectares(wkt: string, exclusionWkts: string[], numberOfDecimals = 2): number | null {
        if (ObjectUtils.isNullOrUndefined(wkt)) {
            return null;
        }

        const totalArea = this.getAreaWkt(wkt);

        if (totalArea === 0) {
            return 0;
        }

        const exclusionGeometries = exclusionWkts
            .map(exclusionWkt => this.terraformerService.wktToArcgis(exclusionWkt, this.spatialReference))
            .filter(geom => !ObjectUtils.isNullOrUndefined(geom));

        if (exclusionGeometries.length === 0) {
            return Number(totalArea.toFixed(numberOfDecimals));
        }

        const geometry = this.terraformerService.wktToArcgis(wkt, this.spatialReference);
        const unionOfExclusions = unionOperator.executeMany(exclusionGeometries);

        if (ObjectUtils.isNullOrUndefined(unionOfExclusions)) {
            return Number(totalArea.toFixed(numberOfDecimals));
        }

        const intersection = intersectionOperator.execute(geometry, unionOfExclusions);
        const exclusionArea = intersection ? areaOperator.execute(intersection, { unit: "hectares" }) : 0;

        return Number((totalArea - exclusionArea).toFixed(numberOfDecimals));
    }

    private fromWicket(geowicket: IMapGeometryWicket, layerColor?: any, symbols?: any[], icons?: IMapIcon[]): Graphic {
        if (ObjectUtils.isNullOrUndefined(geowicket?.wkt)) {
            console.warn("IMapGeometryWicket contains no wkt-string", geowicket);
            return null;
        }

        const [geometry, type] = this.geometryFromWkt(geowicket.wkt);

        return new Graphic({
            geometry: geometry,
            attributes: this.getMetadata(geowicket),
            symbol: this.getSymbol(geowicket, type, layerColor, symbols, icons)
        });
    }

    private geometryFromWkt(wkt: string): [Geometry, string] {
        const geoJson = this.terraformerService.wktToGeoJson(wkt);
        const arcGisGeometry = this.terraformerService.geojsonToArcGIS(geoJson, this.spatialReference);
        switch (geoJson.type) {
            case "LineString":
            case "MultiLineString":
                return [
                    new Polyline(arcGisGeometry),
                    "polyline"
                ];
            case "Polygon":
            case "MultiPolygon":
                return [
                    new Polygon(arcGisGeometry),
                    "polygon"
                ];
            case "Point":
                return [
                    new Point(arcGisGeometry),
                    "point"
                ];
            case "MultiPoint":
                return [
                    new Multipoint(arcGisGeometry),
                    "multipoint"
                ];
            default:
                throw Error(`Geometry type: ${geoJson.type} is not implemented`);
        }
    }

    private fromXY(geopoint: IMapGeometryPoint, layerColor?: any, symbols?: any[], icons?: IMapIcon[]): Graphic {
        const getSymbol = () => {
            if (geopoint.styleName) {
                return this.symbolService.getSymbolByStyleName(geopoint.styleName, true);
            } else if (geopoint.symbolText) {
                return this.symbolService.fromSymbolText(geopoint);
            } else {
                return this.symbolService.getSymbol({
                    type: "point",
                    size: geopoint.size ?? null,
                    colors: [
                        geopoint.color,
                        layerColor
                    ],
                    iconid: geopoint.iconid,
                    style: geopoint.symbolStyle,
                    selected: geopoint.selected,
                    selectedColor: "cyan"
                }, symbols, icons);
            }
        };

        return new Graphic({
            geometry: new Point({
                x: geopoint.easting,
                y: geopoint.northing,
                spatialReference: this.spatialReference
            }),
            attributes: this.getMetadata(geopoint),
            symbol: getSymbol()
        });
    }

    private fromLine(geoline: IMapGeometryLine, layerColor?: any): Graphic {
        return new Graphic({
            geometry: new Polyline({
                paths: [
                    [
                        [
                            geoline.startEasting,
                            geoline.startNorthing
                        ],
                        [
                            geoline.endEasting,
                            geoline.endNorthing
                        ]
                    ]
                ],
                spatialReference: this.spatialReference
            }),
            attributes: this.getMetadata(geoline),
            symbol: geoline.styleName
                ? this.symbolService.getSymbolByStyleName(geoline.styleName, true)
                : this.symbolService.getSymbol({
                    type: "polyline",
                    size: 1,
                    colors: [
                        geoline.color,
                        layerColor
                    ],
                    selected: geoline.selected,
                    selectedColor: "cyan"
                })
        });
    }

    private getSymbol(geowicket: IMapGeometryWicket, type, layerColor?, symbols?: any[], icons?: IMapIcon[]) {
        let result: any;
        if (geowicket.styleName) {
            result = this.symbolService.getSymbolByStyleName(geowicket.styleName, true);
        } else {
            result = this.symbolService.getSymbol({
                type: type,
                colors: [
                    geowicket.color,
                    layerColor
                ],
                iconid: geowicket.iconid,
                selected: geowicket.selected,
                selectedColor: "cyan"
            }, symbols, icons);
        }
        result.xoffset = geowicket.xoffset ?? result.xoffset;
        result.yoffset = geowicket.yoffset ?? result.yoffset;
        return result;
    }

    private getMetadata(geo: IMapGeometry): any {
        const geoId = !ObjectUtils.isNullOrUndefined(geo.geoid) ? geo.geoid : this.generateGeoId();
        geo.geoid = geoId;
        return {
            id: geo.id,
            label: geo.label,
            geoid: geoId,
            selected: geo.selected,
            tag: geo.tag,
            isDrawing: geo.isDrawing
        };
    }

}
